import { DragEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FileSpreadsheet, FileText, Image as Bilde, ShieldAlert, Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { ENDELSER, filApi, type Dokumentstatistikk, type Fil, type Forslag } from '../../api/filer';
import ForslagKort from './ForslagKort';

interface Props {
  /** Feil som ikke hører hjemme på siden, som at økten har gått ut. Gir om feilen ble tatt hånd om. */
  onFeil: (e: unknown) => boolean;
  /** Millisekunder mellom hver gang en fil som leses, hentes på nytt. */
  ventetid?: number;
}

interface Valgt {
  fil: File;
  beskrivelse: string;
  feil: string | null;
}

/** Hvor mange ganger en fil hentes på nytt før siden slutter å vente. Med 2 sekunder er det to minutter. */
const MAKS_FORSOK = 60;
const EKSEMPLER = 5;
const REGNEARK = /\.(csv|xlsx)$/i;

export const storrelse = (byte: number) => (byte < 1024 * 1024 ? `${Math.max(1, Math.round(byte / 1024))} kB` : `${(byte / 1024 / 1024).toFixed(1)} MB`);

export function Ikon({ filnavn }: { filnavn: string }) {
  const klasse = 'h-5 w-5';
  if (REGNEARK.test(filnavn)) return <FileSpreadsheet aria-hidden="true" className={klasse} />;
  if (/\.(png|jpe?g)$/i.test(filnavn)) return <Bilde aria-hidden="true" className={klasse} />;
  return <FileText aria-hidden="true" className={klasse} />;
}

/**
 * Dokumentasjonen (T100): kunden laster opp det hen har, uten mal. Det kan være vareregister og saldo, ordre- og
 * transaksjonshistorikk, lagerkart, bilder og rutiner. Alt leses av dokumentleseren (T101), som sier hva dokumentet
 * er og hva det viser. Tallene i regneark regnes ut av koden. Innholdet valideres ikke (beslutning 0051).
 */
export default function DokumentasjonSide({ onFeil, ventetid = 2000 }: Props) {
  const { t, i18n } = useTranslation();
  const [filer, setFiler] = useState<Fil[] | null>(null);
  const [valgte, setValgte] = useState<Valgt[]>([]);
  const [laster, setLaster] = useState(false);
  const [dras, setDras] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const [forslag, setForslag] = useState<Forslag[]>([]);
  const [lagrer, setLagrer] = useState(false);
  const velger = useRef<HTMLInputElement>(null);

  const handter = useCallback(
    (e: unknown) => {
      if (!onFeil(e)) setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    },
    [onFeil]
  );

  const hentForslag = useCallback(() => {
    filApi
      .forslag()
      .then((liste) => setForslag(Array.isArray(liste) ? liste : []))
      .catch(() => setForslag([]));
  }, []);

  useEffect(() => {
    filApi
      .dokumentasjon()
      .then((data) => setFiler(data.filer))
      .catch(handter);
    hentForslag();
  }, [handter, hentForslag]);

  async function velgForslag(f: Forslag, bekreft: boolean) {
    setLagrer(true);
    try {
      if (bekreft) await filApi.bekreftForslag(f);
      else await filApi.avvisForslag(f);
    } catch (e) {
      handter(e);
    } finally {
      setLagrer(false);
      hentForslag();
    }
  }

  // Filer som leses, hentes på nytt til de er ferdige
  const venter = (filer ?? []).filter((f) => f.status === 'mottatt').map((f) => f.id).join(',');
  useEffect(() => {
    if (!venter) return;
    let forsok = 0;
    let avbrutt = false;
    let tidtaker: ReturnType<typeof setTimeout>;
    const hent = () => {
      tidtaker = setTimeout(async () => {
        try {
          const oppdatert = await Promise.all(venter.split(',').map((id) => filApi.fil(id)));
          if (avbrutt) return;
          const ferdige = oppdatert.filter((f) => f.status !== 'mottatt');
          if (ferdige.length > 0) {
            setFiler((liste) => (liste ?? []).map((f) => ferdige.find((n) => n.id === f.id) ?? f));
            hentForslag();
          }
          else if (++forsok < MAKS_FORSOK) hent();
        } catch (e) {
          if (!avbrutt) handter(e);
        }
      }, ventetid);
    };
    hent();
    return () => {
      avbrutt = true;
      clearTimeout(tidtaker);
    };
  }, [venter, ventetid, handter, hentForslag]);

  function leggTil(liste: FileList | null) {
    if (!liste) return;
    // Kopier filene før feltet tømmes. FileList er levende, og blir tom når feltet tømmes
    const nye = Array.from(liste).map((fil) => ({ fil, beskrivelse: '', feil: null }));
    setFeil(null);
    setValgte((v) => [...v, ...nye]);
    if (velger.current) velger.current.value = '';
  }

  function slipp(e: DragEvent) {
    e.preventDefault();
    setDras(false);
    leggTil(e.dataTransfer.files);
  }

  async function lastOpp(e: FormEvent) {
    e.preventDefault();
    if (valgte.length === 0 || laster) return;
    setFeil(null);
    setLaster(true);
    const igjen: Valgt[] = [];
    for (const v of valgte) {
      try {
        const lagret = await filApi.lastOpp(v.fil, { beskrivelse: v.beskrivelse });
        setFiler((liste) => [lagret, ...(liste ?? [])]);
      } catch (feilen) {
        if (onFeil(feilen)) return;
        igjen.push({ ...v, feil: feilen instanceof ApiFeil ? feilen.kode : 'ukjent' });
      }
    }
    setValgte(igjen);
    setLaster(false);
  }

  const dato = (iso: string) => new Date(iso).toLocaleDateString(i18n.resolvedLanguage, { day: 'numeric', month: 'short' });

  return (
    <section aria-labelledby="dokumentasjon-tittel" className="flex flex-col gap-6">
      <div className="kort p-6 sm:p-10">
        <h2 id="dokumentasjon-tittel" className="mb-3 text-[1.625rem] font-semibold">
          {t('reise.dokumentasjon.tittel')}
        </h2>
        <p className="mb-6 max-w-lesbar text-slate-600">{t('reise.dokumentasjon.ingress')}</p>

        <p className="mb-8 flex max-w-lesbar gap-3 melding-info text-sm">
          <ShieldAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <span>{t('reise.dokumentasjon.personvern')}</span>
        </p>

        <form onSubmit={lastOpp}>
          <label
            htmlFor="dokumentasjon-filer"
            onDragOver={(e) => {
              e.preventDefault();
              setDras(true);
            }}
            onDragLeave={() => setDras(false)}
            onDrop={slipp}
            className={
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ' +
              (dras ? 'border-aksent-600 bg-aksent-50' : 'border-slate-300 bg-slate-50/60 hover:border-slate-400 hover:bg-slate-50')
            }
          >
            <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-600 shadow-sm">
              <Upload className="h-5 w-5" />
            </span>
            <span className="font-medium text-slate-900">{t('reise.dokumentasjon.velg')}</span>
            <span className="text-sm text-slate-500">{t('reise.dokumentasjon.formater')}</span>
          </label>
          <input
            ref={velger}
            id="dokumentasjon-filer"
            type="file"
            multiple
            accept={ENDELSER.dokumentasjon}
            onChange={(e) => leggTil(e.target.files)}
            className="sr-only"
          />

          {valgte.length > 0 && (
            <ul className="mt-6 flex flex-col gap-3">
              {valgte.map((v, i) => (
                <li key={`${v.fil.name}-${i}`} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500">
                      <Ikon filnavn={v.fil.name} />
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-900">{v.fil.name}</span>
                    <span className="text-sm tabular-nums text-slate-500">{storrelse(v.fil.size)}</span>
                    <button
                      type="button"
                      onClick={() => setValgte((liste) => liste.filter((_, j) => j !== i))}
                      aria-label={t('reise.dokumentasjon.fjern', { fil: v.fil.name })}
                      className="knapp-stille min-h-0 p-1.5"
                    >
                      <X aria-hidden="true" className="h-4 w-4" />
                    </button>
                  </div>
                  <label htmlFor={`beskrivelse-${i}`} className="sr-only">
                    {t('reise.dokumentasjon.beskrivelse', { fil: v.fil.name })}
                  </label>
                  <input
                    id={`beskrivelse-${i}`}
                    type="text"
                    maxLength={500}
                    value={v.beskrivelse}
                    placeholder={t('reise.dokumentasjon.beskrivelseHjelp')}
                    onChange={(e) => setValgte((liste) => liste.map((x, j) => (j === i ? { ...x, beskrivelse: e.target.value } : x)))}
                    className="felt mt-3 w-full text-sm"
                  />
                  {v.feil && (
                    <p role="alert" className="mt-3 melding-feil text-sm">
                      {t(`feil.${v.feil}`, { defaultValue: t('feil.ukjent') })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {valgte.length > 0 && (
            <div className="mt-6 flex justify-end">
              <button type="submit" disabled={laster} className="knapp-primaer">
                {laster ? t('reise.dokumentasjon.lasterOpp') : t('reise.dokumentasjon.lastOpp', { count: valgte.length, antall: valgte.length })}
              </button>
            </div>
          )}
          {feil && (
            <p role="alert" className="mt-4 melding-feil">
              {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
            </p>
          )}
        </form>
      </div>

      {forslag.length > 0 && (
        <section aria-labelledby="dokumentasjon-forslag" className="kort p-6 sm:p-8">
          <h3 id="dokumentasjon-forslag" className="mb-1 text-lg font-semibold">
            {t('reise.forslag.liste', { count: forslag.length, antall: forslag.length })}
          </h3>
          <p className="mb-5 text-sm text-slate-600">{t('reise.forslag.ingress')}</p>
          <ul className="flex flex-col gap-3">
            {forslag.map((f) => (
              <li key={`${f.filId}|${f.sporsmalId}|${f.instans}`}>
                <ForslagKort forslag={f} lagrer={lagrer} visSporsmal onBekreft={(x) => velgForslag(x, true)} onAvvis={(x) => velgForslag(x, false)} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {filer && filer.length > 0 && (
        <section aria-labelledby="dokumentasjon-lastet" className="kort p-6 sm:p-8">
          <h3 id="dokumentasjon-lastet" className="mb-4 text-lg font-semibold">
            {t('reise.dokumentasjon.lastet', { count: filer.length, antall: filer.length })}
          </h3>
          <ul className="flex flex-col divide-y divide-slate-100">
            {filer.map((f) => (
              <Dokument key={f.id} fil={f} dato={dato(f.opplastet)} />
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function Dokument({ fil, dato }: { fil: Fil; dato: string }) {
  const { t } = useTranslation();
  const status =
    fil.status === 'feilet'
      ? { tekst: t('reise.dokumentasjon.status.feilet'), klasse: 'bg-red-50 text-red-800' }
      : fil.status === 'mottatt'
        ? { tekst: t('reise.dokumentasjon.status.leses'), klasse: 'bg-slate-100 text-slate-600' }
        : { tekst: t('reise.dokumentasjon.status.lest'), klasse: 'bg-aksent-50 text-aksent-800' };
  const tolkning = fil.tolkning;

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-slate-500">
          <Ikon filnavn={fil.filnavn} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-slate-900">{fil.filnavn}</p>
          {fil.beskrivelse && <p className="text-sm text-slate-600">{fil.beskrivelse}</p>}
          <p className="text-xs tabular-nums text-slate-500">
            {storrelse(fil.storrelse)} · {dato}
            {tolkning && <> · {t(`reise.dokumentasjon.type.${tolkning.dokumenttype}`, { defaultValue: tolkning.dokumenttype })}</>}
          </p>
          {tolkning && (
            <div className="mt-3 max-w-lesbar">
              <p className="text-slate-700">{tolkning.oppsummering}</p>
              {tolkning.statistikk && <Tall statistikk={tolkning.statistikk} />}
              {tolkning.funn.length > 0 && (
                <ul className="mt-3 flex flex-col gap-1.5 text-sm text-slate-600">
                  {tolkning.funn.map((f, i) => (
                    <li key={i} className="flex gap-2">
                      <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-aksent-600" />
                      <span>{f.tekst}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <span role="status" className={'merke shrink-0 ' + status.klasse}>
          {status.tekst}
        </span>
      </div>
      {fil.status === 'feilet' && (
        <div className="ml-8 mt-3">
          <Resultat fil={fil} />
        </div>
      )}
    </li>
  );
}

/** Tallene koden regnet ut fra regnearket. Bare de som finnes, vises. */
export function Tall({ statistikk }: { statistikk: Dokumentstatistikk }) {
  const { t, i18n } = useTranslation();
  const tall = (n: number) => n.toLocaleString(i18n.resolvedLanguage);
  const rader: [string, string][] = [];
  if (statistikk.varer !== null) rader.push([t('reise.dokumentasjon.tall.varer'), tall(statistikk.varer)]);
  if (statistikk.lokasjoner !== null) rader.push([t('reise.dokumentasjon.tall.lokasjoner'), tall(statistikk.lokasjoner)]);
  if (statistikk.lokasjonsformat) rader.push([t('reise.dokumentasjon.tall.lokasjonsformat'), statistikk.lokasjonsformat]);
  if (statistikk.ordrePerDag !== null) rader.push([t('reise.dokumentasjon.tall.ordrePerDag'), tall(statistikk.ordrePerDag)]);
  if (statistikk.linjerPerDag !== null) rader.push([t('reise.dokumentasjon.tall.linjerPerDag'), tall(statistikk.linjerPerDag)]);
  if (statistikk.flestOrdrePaEnDag !== null) rader.push([t('reise.dokumentasjon.tall.flest'), tall(statistikk.flestOrdrePaEnDag)]);
  if (statistikk.fra && statistikk.til) rader.push([t('reise.dokumentasjon.tall.periode'), `${statistikk.fra} – ${statistikk.til}`]);
  if (rader.length === 0) return null;

  return (
    <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-slate-50 px-4 py-3 text-sm sm:grid-cols-3">
      {rader.map(([navn, verdi]) => (
        <div key={navn}>
          <dt className="text-xs text-slate-500">{navn}</dt>
          <dd className="font-semibold tabular-nums text-slate-900">{verdi}</dd>
        </div>
      ))}
    </dl>
  );
}

/** En fil som ikke kunne leses, med årsaken. Innholdet valideres ikke (beslutning 0051), så andre filer har ingenting her. */
export function Resultat({ fil }: { fil: Fil }) {
  const { t } = useTranslation();
  if (fil.status !== 'feilet') return null;

  return (
    <p role="alert" className="melding-feil">
      {t(`reise.lagerfil.lesefeil.${fil.feilkode}`, { defaultValue: t('reise.lagerfil.lesefeil.ukjent'), fil: fil.filnavn })}
    </p>
  );
}
